<template>
  <WorkBasicDialog
    ref="workBasicDialog"
    :width="width"
    :height="height"
    :no-simple-close="noSimpleClose"
    :addition-to-default-close="_resetFormDialog"
    :content-class="contentClass"
    :fixed-height="fixedHeight"
    :no-header="noHeader"
    v-on="$listeners"
    @close:dialog="$emit('close:dialog')"
  >
    <template #header>
      <slot name="header">
        <span v-text="headerLabel" />
      </slot>
    </template>

    <template #header-append>
      <div class="error-monitor-wrapper">
        <ErrorMonitor :errors="serversideErrors.mappedErrors" />
      </div>

      <slot name="header-append" />
    </template>

    <template #content-wrapper>
      <slot name="content-wrapper">
        <ValidationObserver
          ref="inputObserver"
          :disabled="true"
        >
          <slot name="content" />
        </ValidationObserver>
      </slot>
    </template>

    <template #dialog-actions="{ close }">
      <slot
        name="dialog-actions"
        :trigger-resolve="triggerResolve"
        :trigger-reject="triggerReject"
        :close="close"
        :submit-pending="submitPending"
      >
        <slot
          name="abort-action"
          :trigger-resolve="triggerResolve"
          :trigger-reject="triggerReject"
          :close="close"
        >
          <w-btn
            outlined
            @click="close"
          >
            <slot name="abort-action-text">
              {{ $t('Dialogs.BasicDialog.abort') }}
            </slot>
          </w-btn>
        </slot>
        <slot
          name="accept-action"
          :trigger-resolve="triggerResolve"
          :trigger-reject="triggerReject"
          :close="close"
          :submit-pending="submitPending"
        >
          <w-btn
            :loading="submitPending"
            @click="triggerResolve"
          >
            <slot name="accept-action-text">
              {{ acceptActionText ?? $t('Dialogs.ConfirmationDialog.confirm') }}
            </slot>
          </w-btn>
        </slot>
      </slot>
    </template>
  </WorkBasicDialog>
</template>

<script>
// components
import WorkBasicDialog from '../WorkBasicDialog';
// services & helpers
import { ValidationObserver } from 'vee-validate';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import formUtils from '@/utils/form-utils';
import axios from 'axios';

export default {
  name: 'WorkFormDialog',
  components: {
    WorkBasicDialog,
    ErrorMonitor: () => import('../../ErrorMonitor'),
    ValidationObserver,
  },
  provide() {
    return {
      formFields: this.formFields,
    };
  },
  props: {
    ...WorkBasicDialog.props,
    promiseBasedApiRequestHandlerMethod: { type: Function, required: true },
    overrideHandleResolve: { type: Function, required: false },
    value: { type: Object, default: () => { } },
    withServersideValidation: { type: Boolean, default: false },
    formData: { type: Object, default: () => { } },
    pathOfFormDataElement: { type: String, required: false },
    serverSideValidationPathType: { type: String, required: false },
    alwaysUseUuidToValidate: { type: Boolean, default: false },
    getDataReference: { type: Function, default: undefined },
    validateServersideCustomObject: { type: Function, default: undefined },
    contentClass: { type: String, default: '' },
    fixedHeight: { type: Boolean, default: false },
    showAllErrors: { type: Boolean, default: false },
    title: { type: String, default: '' },
    isNew: { type: Boolean, default: false },
    isEdit: { type: Boolean, default: false },
    noHeader: { type: Boolean, default: false },
    titleAppend: { type: Boolean, default: true },
    simpleTitle: { type: String },
    acceptActionText: { type: String, default: undefined },
  },
  data() {
    return {
      dataReference: {},
      serversideErrors: {},
      formFields: {},
      confirmations: [],
      submitPending: false,
    };
  },
  computed: {
    dataWrapper: {
      set(value) {
        this.$emit('input', value);
      },
      get() {
        return { data: this.value };
      },
    },
    headerLabel() {
      if (!this.titleAppend) return this.title;
      if (this.simpleTitle) return this.simpleTitle;
      if (this.isNew == true && this.isEdit == false)
        return this.title + ' ' + this.$t('Common.create');
      if (this.isEdit == true && this.isNew == false)
        return this.title + ' ' + this.$t('Components.sentences.edit');
      return this.title + ' ' + this.$t('Common.choose');
    },
  },
  watch: {
    withServersideValidation: {
      handler(value) {
        if (value === true) {
          if (this.pathOfFormDataElement == undefined) {
            window.console.error(
              'path-of-form-data-element is missing (beacuse with-serverside-validation is true)'
            );
          }
          if (this.serverSideValidationPathType == undefined) {
            window.console.error(
              'server-side-validation-path-type is missing (beacuse with-serverside-validation is true)'
            );
          }
        }
      },
      immediate: true,
    },
  },
  methods: {
    /********************************
     * ### EXTENDED LOGIC ###
     *******************************/
    open(dataReference) {
      this.dataReference = dataReference;
      return this.$refs.workBasicDialog.open();
    },
    close() {
      return this.$refs.workBasicDialog.close();
    },
    triggerResolve() {
      if (this.overrideHandleResolve != undefined) {
        return this.overrideHandleResolve(
          this.$refs.workBasicDialog.triggerResolve,
          this.$refs.inputObserver
        );
      }
      return this._handleResolve(
        this.$refs.workBasicDialog.triggerResolve,
        this.$refs.inputObserver
      );
    },
    triggerReject() {
      this.$refs.workBasicDialog.triggerReject();
    },
    setErrors(errors) {
      this.serversideErrors = errors;
    },
    /********************************
     * ### INTERNAL LOGIC ###
     *******************************/
    _handleResolve(triggerResolve, observer) {
      this._validateInputLocal(observer).then(async (formIsValid) => {
        if (formIsValid === false) {
          return;
        }

        this.submitPending = true;
        if (this.withServersideValidation === true) {
          const serversideErrors = await this._validateViaServer();
          if (serversideErrors === false) {
            this.submitPending = false;
            return;
          }
          else if (typeof serversideErrors === 'object') {
            this.submitPending = false;
            this._handleError(serversideErrors);
            return
          }
        }

        try {
          const response = await this.promiseBasedApiRequestHandlerMethod(
            this.dataWrapper
          );
          this._resetFormDialog();
          this.dataWrapper = { data: this.value };
          triggerResolve(response);
        } catch (error) {
          this._handleError(error);
        } finally {
          this.submitPending = false;
        }
      });
    },
    _handleError(error, id = undefined) {
      const errorResponse = error.response;
      if (errorResponse?.isValidationError) {
        return errorResponse.handler.execute(
          this.formFields,
          this.$refs.inputObserver,
          id,
          (newErrors) => (this.serversideErrors = newErrors),
          false
        );
      } else if (errorResponse?.isConfirmationError) {
        return errorResponse?.handler.execute(
          this.handleAcceptedConfirmation,
          this.confirmations,
          (newConfirmations) => (this.confirmations = newConfirmations)
        );
      }

      throw error;
    },
    _resetFormDialog() {
      this.$refs.inputObserver?.reset();
      this._resetServersideErrors();
    },
    _resetServersideErrors() {
      this.serversideErrors = {};
    },
    invokeGetDataReference() {
      if (this.getDataReference) {
        return this.getDataReference();
      }
      return this.dataReference;
    },
    /********************************
     * ### LOCAL VALIDATION ###
     *******************************/
    _validateInputLocal(observer) {
      this._resetFormDialog();
      return observer.validate();
    },
    handleAcceptedConfirmation(confirmations) {
      this.dataWrapper.state = { confirmations: confirmations };
      this.triggerResolve();
      this.$emit('confirmationGiven', true);
    },
    /********************************
     * ### SERVERSIDE VALIDATION ###
     *******************************/
    async _validateViaServer() {
      try {
        this._resetServersideErrors();
        let localFormDataCopy = cloneDeep(this.formData);
        localFormDataCopy = this._updateFormCopyHandler(
          localFormDataCopy,
          this.invokeGetDataReference()
        );
        if (this.validateServersideCustomObject) {
          localFormDataCopy = this.validateServersideCustomObject();
        }

        await this._invokeServersideValidation(localFormDataCopy);
        return true;
      } catch (error) {
        if (error.response?.isValidationError) {
          return this._evaluateServersideValidation(error);
        }

        return error;
      }
    },
    _evaluateServersideValidation(error) {
      const errorResponse = error.response;
      let dataId = null;

      if (this.validateServersideCustomObject)
        dataId =
          this.validateServersideCustomObject().id ??
          this.validateServersideCustomObject().uuid;
      else
        dataId =
          this.invokeGetDataReference().id ??
          this.invokeGetDataReference().uuid;

      if (this.alwaysUseUuidToValidate === true) {
        dataId = this.invokeGetDataReference().uuid;
      }

      const globalHandler = errorResponse.handler;
      globalHandler.setMappedErrors(
        this.formFields,
        this.showAllErrors ? undefined : dataId
      );
      const noErrorsForThisObject = globalHandler.mappedErrors.length <= 0;

      if (noErrorsForThisObject) {
        return true;
      }

      globalHandler
        .setErrorsIntoObserver(this.$refs.inputObserver)
        .invokeCacheErrors((newErrors) => (this.serversideErrors = newErrors));
      return false;
    },
    _updateFormCopyHandler(localFormDataCopy, dataReference) {
      const currentProp = get(localFormDataCopy, this.pathOfFormDataElement);

      if (Array.isArray(currentProp) === true) {
        this.currentProp = this._arrayUpdateHandler(currentProp, dataReference);
      } else {
        this.currentProp = this._defaultUpdateHandler(
          currentProp,
          dataReference
        );
      }

      return localFormDataCopy;
    },
    _invokeServersideValidation(data) {
      if (!this.serverSideValidationPathType) return;
      const formId = data.id ?? data.uuid;
      if (formId && formUtils.isNewFormId(formId)) {
        return axios.post(
          `/rest/v4/${this.serverSideValidationPathType}?validateOnly=true`,
          data
        );
      }
      if (formId) {
        return axios.put(
          `/rest/v4/${this.serverSideValidationPathType}/${formId}?validateOnly=true`,
          data
        );
      }
    },
    /********************************
     * ### FORMDATA UPDATE HANDLER ###
     *******************************/
    _arrayUpdateHandler(prop, newDataValue) {
      const index = prop.findIndex((_item) => {
        const _itemId = _item.id || _item.uuid;
        const newValueId = newDataValue.id || newDataValue.uuid;
        return _itemId == newValueId;
      });

      if (index >= 0) {
        prop[index] = newDataValue;
      } else {
        prop.push(newDataValue);
      }
      return prop;
    },
    _defaultUpdateHandler(prop, newDataValue) {
      this.prop = newDataValue;
      return prop;
    },
  },
};
</script>